import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { listEvents } from "@/lib/events";
import { listFollowingIds } from "@/lib/social";
import { useAuth } from "@/context/AuthContext";
import EventCard from "@/components/EventCard";
import { Plus, Search, X, Crosshair, MapPin } from "lucide-react";
import { CATEGORIES as INONIT_CATEGORIES } from "@/lib/categories";
import { geocodeCities } from "@/lib/cityGeocode";
import { toast } from "sonner";
import PeopleResults from "@/components/PeopleResults";

const CATEGORIES = [{ key: "All", emoji: "✦" }, ...INONIT_CATEGORIES.map((c) => ({ key: c.key, emoji: c.emoji }))];

const DATE_FILTERS = [
  { key: "any",   label: "Anytime" },
  { key: "today", label: "Today" },
  { key: "week",  label: "This week" },
  { key: "month", label: "This month" },
];

// Radius options in km. `null` = anywhere (no spatial filter).
const RADII = [
  { key: "5",   label: "5 km",   km: 5 },
  { key: "10",  label: "10 km",  km: 10 },
  { key: "25",  label: "25 km",  km: 25 },
  { key: "50",  label: "50 km",  km: 50 },
  { key: "100", label: "100 km", km: 100 },
  { key: "any", label: "Anywhere", km: null },
];

function inDateRange(iso, key) {
  if (key === "any") return true;
  const d = new Date(iso);
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start);
  if (key === "today") end.setDate(end.getDate() + 1);
  else if (key === "week") end.setDate(end.getDate() + 7);
  else if (key === "month") end.setMonth(end.getMonth() + 1);
  return d >= start && d < end;
}

export default function FeedPage() {
  const { user, profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "All";
  const radiusKey = searchParams.get("r") || "25"; // default 25 km
  const q = searchParams.get("q") || "";
  const dateKey = searchParams.get("when") || "any";
  const onlyFollowing = searchParams.get("from") === "following";

  // Live location toggle — starts as null (use profile location), turns into
  // {lat, lng} when the user opts-in via the "Use my location" button.
  // Persisted in sessionStorage so it survives navigation to event detail
  // and back (component unmount would otherwise reset it).
  const LIVE_LOC_KEY = "inonit:liveLoc";
  const [liveLoc, setLiveLoc] = useState(() => {
    try {
      const raw = sessionStorage.getItem(LIVE_LOC_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.lat === "number" && typeof parsed.lng === "number") {
        return parsed;
      }
    } catch {}
    return null;
  });
  useEffect(() => {
    try {
      if (liveLoc) sessionStorage.setItem(LIVE_LOC_KEY, JSON.stringify(liveLoc));
      else sessionStorage.removeItem(LIVE_LOC_KEY);
    } catch {}
  }, [liveLoc]);
  const [locBusy, setLocBusy] = useState(false);

  // Fallback resolved-from-city coords for pre-v8 users whose profile has a
  // `city` string but no `location_lat`/`location_lng`. We geocode their
  // city once per session (via the shared cityGeocode cache so the same
  // city → coord lookup is reused across the feed's event-side resolution).
  // The admin backfill page populates these permanently in the DB; this is
  // the runtime safety net until they're backfilled.
  const [profileGeocoded, setProfileGeocoded] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setProfileGeocoded(null);
    if (!profile) return;
    if (profile.location_lat != null && profile.location_lng != null) return;
    if (!profile.city) return;
    geocodeCities([profile.city]).then((m) => {
      if (cancelled) return;
      const v = m.get(profile.city.trim().toLowerCase());
      if (v) setProfileGeocoded(v);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [profile?.location_lat, profile?.location_lng, profile?.city]); // eslint-disable-line react-hooks/exhaustive-deps

  const [events, setEvents] = useState([]);
  const [followingIds, setFollowingIds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(q);
  const debounceRef = useRef();

  // Resolve the discovery centre, in priority order:
  //   1. Live geolocation (user explicitly clicked "Use my location")
  //   2. Saved profile coordinates (set by city-picker at signup, or backfill)
  //   3. Runtime-geocoded profile city (pre-v8 fallback, this session only)
  //   4. null → "Anywhere" mode
  const center = useMemo(() => {
    if (liveLoc) return liveLoc;
    if (profile?.location_lat != null && profile?.location_lng != null) {
      return { lat: profile.location_lat, lng: profile.location_lng };
    }
    if (profileGeocoded) return profileGeocoded;
    return null;
  }, [liveLoc, profile?.location_lat, profile?.location_lng, profileGeocoded]);

  const radius = useMemo(
    () => {
      const found = RADII.find((r) => r.key === radiusKey);
      // `?? 25` only applies when `km` is undefined (unknown key). For the
      // "Anywhere" pill `km` is explicitly null, which must survive.
      return found ? found.km : 25;
    },
    [radiusKey]
  );

  // City hint that lets the listEvents client-side filter keep legacy events
  // that have no lat/lng (matches `event.city` to the user's centre city).
  // Skipped when using live geolocation — we don't know which city that is.
  const centerCity = useMemo(() => {
    if (liveLoc) return null;
    return profile?.city || null;
  }, [liveLoc, profile?.city]);

  useEffect(() => {
    if (!user?.id) { setFollowingIds(null); return; }
    listFollowingIds(user.id).then(setFollowingIds).catch(() => setFollowingIds([]));
  }, [user?.id]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    listEvents({ category, q, signal: ctrl.signal, center, radiusKm: radius, centerCity })
      .then((data) => setEvents(data))
      .catch((err) => { if (err?.name !== "AbortError") setEvents([]); })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [category, q, center, radius, centerCity]);

  useEffect(() => {
    if (searchInput === q) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => updateParam("q", searchInput), 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Per-param "default" sentinels. When a click matches the default, we
  // drop the param from the URL to keep it clean. Crucially, the radius
  // param's default is `"25"` — clicking "Anywhere" (key `"any"`) must NOT
  // be treated as a default-deselect, otherwise the URL collapses to empty
  // and the radius falls back to 25 km, making the "Anywhere" pill un-usable.
  const PARAM_DEFAULTS = { category: "All", when: "any", from: null, r: "25", q: null };

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    const def = PARAM_DEFAULTS[key];
    if (value == null || value === "" || value === def) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const useMyLocation = () => {
    // Already on → toggle off (revert to profile city).
    if (liveLoc) {
      setLiveLoc(null);
      toast.message("Switched back to your profile city.");
      return;
    }
    if (!navigator.geolocation) {
      toast.error("Location not available on this device.");
      return;
    }
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLiveLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocBusy(false);
        toast.success("Centered on your current location.");
      },
      () => {
        // User denied — fall back silently to profile location.
        setLocBusy(false);
        toast.message("Using your profile city instead.");
      },
      { enableHighAccuracy: false, timeout: 7000, maximumAge: 5 * 60 * 1000 }
    );
  };

  const filtered = useMemo(() => {
    let arr = events.filter((e) => inDateRange(e.date_time, dateKey));
    if (onlyFollowing && followingIds) {
      const set = new Set(followingIds);
      arr = arr.filter((e) => set.has(e.host_id));
    }
    return arr;
  }, [events, dateKey, onlyFollowing, followingIds]);

  const heading = useMemo(() => {
    if (q) return `Results for "${q}"`;
    if (onlyFollowing) return "From people you follow.";
    if (category !== "All") return `${category}.`;
    return "What's on near you.";
  }, [q, category, onlyFollowing]);

  const locLabel = liveLoc
    ? "Live location"
    : profile?.city
    ? profile.city
    : "Anywhere";

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-5 md:px-10 pt-6 md:pt-10">
        <div className="flex items-end justify-between gap-4 mb-5 md:mb-8">
          <div>
            <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.25em] text-ink/60 mb-2">Feed</span>
            <h1 className="font-display text-[2.4rem] md:text-6xl leading-[0.95] tracking-tight text-ink">{heading}</h1>
          </div>
          <Link to={user ? "/create" : "/login?next=/create"} data-testid="feed-create-button" className="hidden md:inline-flex cta-primary">
            <Plus size={16} strokeWidth={2.8} /> Post a thing
          </Link>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={18} strokeWidth={2.2} className="absolute left-5 top-1/2 -translate-y-1/2 text-ink/45" />
          <input
            type="text" value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search events or people…"
            data-testid="feed-search-input"
            className="chunky-input pl-12 pr-12 rounded-full"
          />
          {searchInput && (
            <button onClick={() => setSearchInput("")} data-testid="feed-search-clear" className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-ink/55 hover:text-ink" aria-label="Clear search">
              <X size={16} strokeWidth={2} />
            </button>
          )}
        </div>

        {/* Inline people search results — visible only when the search box
            has 2+ chars. Sits ABOVE the event grid so users can follow
            without leaving the feed. */}
        <PeopleResults query={searchInput} viewerId={user?.id} />

        {/* Location + radius row */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={useMyLocation}
            disabled={locBusy}
            data-testid="feed-use-my-location"
            className={`inline-flex items-center gap-1.5 border-[2.5px] border-ink rounded-full px-3.5 py-1.5 text-[11px] uppercase tracking-widest font-extrabold transition-colors ${liveLoc ? "bg-lime" : "bg-paper hover:bg-ink hover:text-paper"}`}
            title="Use current location"
          >
            <Crosshair size={12} strokeWidth={2.8} />
            {locBusy ? "Locating…" : liveLoc ? "Live" : "Use my location"}
          </button>
          <span data-testid="feed-location-label" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink/55">
            <MapPin size={12} strokeWidth={2.6} />
            From: <span className="text-ink font-extrabold">{locLabel}</span>
          </span>
        </div>

        <div
          data-testid="feed-radius-row"
          className="-mx-5 md:mx-0 px-5 md:px-0 flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-3"
        >
          {RADII.map((r) => {
            const active = radiusKey === r.key;
            const disabled = r.km != null && !center; // need a centre for spatial filters
            return (
              <button
                key={r.key} type="button"
                disabled={disabled}
                onClick={() => updateParam("r", r.key === "25" ? null : r.key)}
                data-testid={`feed-radius-${r.key}`}
                className={`shrink-0 px-4 py-2 rounded-full border-[2.5px] border-ink text-[11px] uppercase tracking-widest font-extrabold transition-colors ${active ? "bg-ink text-paper" : "bg-paper hover:bg-ink hover:text-paper"} ${disabled ? "opacity-40 cursor-not-allowed hover:bg-paper hover:text-ink" : ""}`}
                title={disabled ? "Add a city to your profile or use my location" : ""}
              >
                {r.label}
              </button>
            );
          })}
        </div>

        {/* Following / Everyone */}
        {user && (
          <div className="mb-3 inline-flex gap-1 p-1 border-[2.5px] border-ink rounded-full bg-paper">
            <FromBtn active={!onlyFollowing} onClick={() => updateParam("from", null)} testid="from-everyone">Everyone</FromBtn>
            <FromBtn active={onlyFollowing} onClick={() => updateParam("from", "following")} testid="from-following">Following</FromBtn>
          </div>
        )}

        {/* Category chips */}
        <div data-testid="feed-filters" className="-mx-5 md:mx-0 px-5 md:px-0 flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {CATEGORIES.map((c) => {
            const active = category === c.key;
            return (
              <button key={c.key} data-testid={`filter-${c.key}`} onClick={() => updateParam("category", c.key)} className={`shrink-0 inline-flex items-center gap-2 border-[2.5px] border-ink rounded-full px-4 py-2.5 text-sm font-extrabold uppercase tracking-widest transition-[transform,background-color,color] ${active ? "bg-ink text-paper" : "bg-paper text-ink hover:bg-ink/5"}`}>
                <span aria-hidden>{c.emoji}</span>{c.key}
              </button>
            );
          })}
        </div>

        {/* Date row */}
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <div className="inline-flex gap-1 p-1 border-[2.5px] border-ink rounded-full bg-paper">
            {DATE_FILTERS.map((d) => (
              <button key={d.key} data-testid={`feed-date-${d.key}`} onClick={() => updateParam("when", d.key)} className={`px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest rounded-full transition-colors ${dateKey === d.key ? "bg-lime text-ink" : "text-ink/65 hover:text-ink"}`}>
                {d.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 pt-8 md:pt-10">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[420px] rounded-3xl border-[3px] border-ink/15 bg-[#EDE7D2] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="feed-empty" className="pop p-10 text-center">
            <p className="font-display text-3xl text-ink">
              {onlyFollowing ? "Nothing from your people." : center ? "Nothing nearby." : "Nothing on yet."}
            </p>
            <p className="text-ink/60 mt-2 mb-6">
              {onlyFollowing
                ? "Follow more hosts or switch to Everyone."
                : center
                ? "Try a wider radius or pick Anywhere."
                : "Add a city to your profile, or post a thing yourself."}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {center && radius !== null && (
                <button onClick={() => updateParam("r", "any")} data-testid="empty-go-anywhere" className="cta-ghost">
                  Search anywhere
                </button>
              )}
              <Link to={onlyFollowing ? "/feed" : "/create"} className="cta-primary">
                {onlyFollowing ? "Browse everyone" : "Post a thing"}
              </Link>
            </div>
          </div>
        ) : (
          <div data-testid="feed-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function FromBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest rounded-full transition-colors ${active ? "bg-ink text-paper" : "text-ink/65 hover:text-ink"}`}>
      {children}
    </button>
  );
}
